import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "./client";
import type { MaterialOption } from "./materialOption";

/**
 * Utdelningsomgångar: utdelarnas svar (kan dela ja/nej + materialval) hör till en omgång.
 * Flera omgångar kan vara öppna samtidigt — augusti och september läggs upp tillsammans så att
 * utdelarna tar ställning till båda i samma formulär. En omgång avslutas explicit; svaren
 * ligger kvar på den stängda omgången.
 */
export interface DistributionRound {
    id: string;
    municipalityPolygonId: string;
    name: string;
    campaignId?: string;
    startedAt: string;
    closedAt?: string;
    yesCount: number;
    noCount: number;
    /** Utdelare som fått material till alla sina uppdrag. */
    materialReceivedCount: number;
    /** Uppdrag (områden) som fått sitt material — kansliet delar ut ett område i taget. */
    assignmentMaterialReceivedCount: number;
}

export interface DistributionRoundList {
    /** Öppna omgångar, äldst först. */
    open: DistributionRound[];
    history: DistributionRound[];
}

export interface DistributionResponse {
    distributionRoundId: string;
    kretsMemberId: string;
    kretsMemberName: string;
    /** null/undefined = inget svar. */
    canDeliver?: boolean | null;
    materialOption?: MaterialOption | null;
    materialReceived: boolean;
    materialReceivedAt?: string;
    respondedAt?: string;
}

/**
 * Vilket flygblad ett uppdrag ska dela ut i en omgång. Valet sitter per uppdrag *och* omgång:
 * utdelarna delar olika flygblad beroende på var de bor, och augusti kan skilja sig från
 * september. Kampanjen följer med flygbladet så massutdelningen kan förvälja båda.
 */
export interface AssignmentFlyerChoice {
    distributionRoundId: string;
    assignmentId: string;
    campaignFlyerId: string;
    campaignId: string;
    campaignName: string;
    flyerName: string;
}

export const fetchOpenAssignmentFlyers = async (
    municipalityId: string
): Promise<AssignmentFlyerChoice[]> => {
    const { data } = await apiClient.get<AssignmentFlyerChoice[]>(
        "/distribution-rounds/assignment-flyers",
        { params: { municipalityId } }
    );
    return data;
};

/** `campaignFlyerId: null` nollställer valet. */
export const setAssignmentFlyer = async (request: {
    roundId: string;
    assignmentId: string;
    campaignFlyerId: string | null;
}): Promise<AssignmentFlyerChoice | null> => {
    const { data } = await apiClient.put<AssignmentFlyerChoice | "">(
        `/distribution-rounds/${request.roundId}/assignments/${request.assignmentId}/flyer`,
        { campaignFlyerId: request.campaignFlyerId }
    );
    // Nollställning svarar 204 utan kropp.
    return data || null;
};

/**
 * Kvitterat material för ett uppdrag i en omgång. Kvitteringen sitter per uppdrag *och*
 * omgång: kansliet delar ut ett område i taget, så en utdelare med tio områden kvitteras
 * tio gånger. Saknas raden är materialet inte utlämnat.
 */
export interface AssignmentMaterialReceipt {
    distributionRoundId: string;
    assignmentId: string;
    receivedAt: string;
}

export const fetchOpenAssignmentMaterials = async (
    municipalityId: string
): Promise<AssignmentMaterialReceipt[]> => {
    const { data } = await apiClient.get<AssignmentMaterialReceipt[]>(
        "/distribution-rounds/assignment-materials",
        { params: { municipalityId } }
    );
    return data;
};

/** `materialReceived: false` tar bort kvitteringen — svaret är då 204 utan kropp. */
export const setAssignmentMaterialReceived = async (request: {
    roundId: string;
    assignmentId: string;
    materialReceived: boolean;
}): Promise<AssignmentMaterialReceipt | null> => {
    const { data } = await apiClient.put<AssignmentMaterialReceipt | "">(
        `/distribution-rounds/${request.roundId}/assignments/${request.assignmentId}/material`,
        { materialReceived: request.materialReceived }
    );
    return data || null;
};

export const fetchDistributionRounds = async (municipalityId: string): Promise<DistributionRoundList> => {
    const { data } = await apiClient.get<DistributionRoundList>("/distribution-rounds", {
        params: { municipalityId }
    });
    return data;
};

export interface CreateDistributionRoundsRequest {
    municipalityPolygonId: string;
    rounds: { name: string; campaignId?: string }[];
    /** Avsluta redan öppna omgångar i samma veva. Standard: låt dem vara öppna. */
    closeOpenRounds?: boolean;
}

export const createDistributionRounds = async (
    request: CreateDistributionRoundsRequest
): Promise<DistributionRound[]> => {
    const { data } = await apiClient.post<DistributionRound[]>("/distribution-rounds", request);
    return data;
};

export const closeDistributionRound = async (roundId: string): Promise<DistributionRound> => {
    const { data } = await apiClient.post<DistributionRound>(`/distribution-rounds/${roundId}/close`);
    return data;
};

/** Svaren för alla öppna omgångar i kommunen — ett anrop räcker för hela utdelningslistan. */
export const fetchOpenDistributionResponses = async (
    municipalityId: string
): Promise<DistributionResponse[]> => {
    const { data } = await apiClient.get<DistributionResponse[]>("/distribution-rounds/responses", {
        params: { municipalityId }
    });
    return data;
};

export const setDistributionMaterialReceived = async (request: {
    roundId: string;
    kretsMemberId: string;
    materialReceived: boolean;
}): Promise<DistributionResponse> => {
    const { data } = await apiClient.put<DistributionResponse>(
        `/distribution-rounds/${request.roundId}/responses/${request.kretsMemberId}/material`,
        { materialReceived: request.materialReceived }
    );
    return data;
};

export const useDistributionRounds = (municipalityId: string) =>
    useQuery({
        queryKey: ["distribution-rounds", municipalityId],
        queryFn: () => fetchDistributionRounds(municipalityId),
        enabled: !!municipalityId,
        staleTime: 60_000
    });

export const useOpenDistributionResponses = (municipalityId: string) =>
    useQuery({
        queryKey: ["distribution-rounds", "responses", municipalityId],
        queryFn: () => fetchOpenDistributionResponses(municipalityId),
        enabled: !!municipalityId,
        staleTime: 30_000
    });

export const useCreateDistributionRounds = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: createDistributionRounds,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["distribution-rounds"] });
        }
    });
};

export const useCloseDistributionRound = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: closeDistributionRound,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["distribution-rounds"] });
        }
    });
};

export const useSetDistributionMaterialReceived = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: setDistributionMaterialReceived,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["distribution-rounds"] });
        }
    });
};

export const useOpenAssignmentFlyers = (municipalityId: string) =>
    useQuery({
        queryKey: ["distribution-rounds", "assignment-flyers", municipalityId],
        queryFn: () => fetchOpenAssignmentFlyers(municipalityId),
        enabled: !!municipalityId,
        staleTime: 30_000
    });

export const useOpenAssignmentMaterials = (municipalityId: string) =>
    useQuery({
        queryKey: ["distribution-rounds", "assignment-materials", municipalityId],
        queryFn: () => fetchOpenAssignmentMaterials(municipalityId),
        enabled: !!municipalityId,
        staleTime: 30_000
    });

export const useSetAssignmentMaterialReceived = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: setAssignmentMaterialReceived,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["distribution-rounds"] });
        }
    });
};

export const useSetAssignmentFlyer = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: setAssignmentFlyer,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["distribution-rounds"] });
            // Massutdelningens källista bär med sig uppdragets flygblad — den måste hämtas om.
            queryClient.invalidateQueries({ queryKey: ["bulk-distribution", "sources"] });
        }
    });
};
